import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';

import type { JsSdkFirebasePlatform } from '@elara/client-core/containers/firebase-platform';

import { initializeAnalytics } from 'firebase/analytics';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  indexedDBLocalPersistence,
  initializeAuth,
} from 'firebase/auth';
import { initializePerformance } from 'firebase/performance';

import { generatePageTitle } from '@elara/client-core/lib/page-title';

import { redactAnalyticsUrl } from '#lib/analytics.js';

/**
 * Web implementation of the Firebase platform seam.
 *
 * Auth mirrors what `getAuth()` configures on web — indexedDB persistence with a localStorage
 * fallback plus the popup redirect resolver that `signInWithPopup` requires — but routes through
 * `initializeAuth` so the persistence choice is explicit and symmetric with the React Native port
 * (which injects `getReactNativePersistence(AsyncStorage)`). See ELARA-689.
 *
 * This module is the only place the web-only `firebase/analytics` and `firebase/performance`
 * imports live, so they never reach the Metro bundle through the shared container.
 */
/** The generic title GA4 reports for every shared itinerary, whatever the tab shows. */
const SHARED_ITINERARY_PAGE_TITLE = generatePageTitle('Shared itinerary');

export const webFirebasePlatform: JsSdkFirebasePlatform = {
  createAuthBackend: (app: FirebaseApp): Auth =>
    initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    }),
  initPerformance: (app, settings) => initializePerformance(app, settings),
  initAnalytics: (app, settings) => {
    // A visitor landing on a shared itinerary must not send its token to GA4 in the automatic first
    // page_view, nor the trip name the page puts in the tab title (gtag sends `document.title` with
    // every hit unless `page_title` is set). The share page is standalone (no in-app navigation),
    // so overriding once suffices.
    const href = window.location.href;
    const pageLocation = redactAnalyticsUrl(href);
    if (pageLocation === href) return initializeAnalytics(app, settings);
    return initializeAnalytics(app, {
      ...settings,
      config: { ...settings?.config, page_location: pageLocation, page_title: SHARED_ITINERARY_PAGE_TITLE },
    });
  },
};
